// Mock fixtures for /admin/releases. Ported from the Claude Design handoff
// at /tmp/design-zwlb0n4/updates-management/project/src/data.jsx and converted to
// TypeScript with the canonical OTA IA (per-app builds, per-app channel pointers).
//
// Real data wiring replaces these one resource at a time as backend phases A/B/C land.
// See docs/plans/ADMIN_RELEASES.md § Backend inventory.

import type {
  AuditEntry,
  ChannelPointer,
  OtaDevice,
} from './types';

export const MOCK_POINTERS: ChannelPointer[] = [
  { channel: 'general', app: 'vn', current: 211, previous: 208, served: 5, maxDevices: 8, minHealthyHours: 72, healthyHours: 18, promotedBy: 'tony', promotedAt: '2026-04-23T22:00Z', policy: 'exposure', haltOnRollback: true, status: 'rolling' },
  { channel: 'beta', app: 'vn', current: 213, previous: 212, served: 4, maxDevices: null, minHealthyHours: 24, healthyHours: 132, promotedBy: 'ci', promotedAt: '2026-04-22T09:20Z', policy: 'open', haltOnRollback: false, status: 'healthy' },

  { channel: 'general', app: 'th', current: 97, previous: 96, served: 21, maxDevices: null, minHealthyHours: 72, healthyHours: 360, promotedBy: 'tony', promotedAt: '2026-04-15T14:12Z', policy: 'open', haltOnRollback: true, status: 'healthy' },
  { channel: 'beta', app: 'th', current: 98, previous: 97, served: 2, maxDevices: null, minHealthyHours: 24, healthyHours: 60, promotedBy: 'ci', promotedAt: '2026-04-21T10:33Z', policy: 'open', haltOnRollback: false, status: 'healthy' },

  { channel: 'general', app: 'vt', current: 44, previous: 43, served: 42, maxDevices: null, minHealthyHours: 72, healthyHours: 480, promotedBy: 'sarah', promotedAt: '2026-04-06T09:00Z', policy: 'open', haltOnRollback: true, status: 'healthy' },
  { channel: 'beta', app: 'vt', current: 45, previous: 44, served: 4, maxDevices: null, minHealthyHours: 24, healthyHours: 130, promotedBy: 'ci', promotedAt: '2026-04-18T12:15Z', policy: 'open', haltOnRollback: false, status: 'healthy' },

  { channel: 'general', app: 'va', current: 30, previous: 29, served: 30, maxDevices: null, minHealthyHours: 72, healthyHours: 504, promotedBy: 'tony', promotedAt: '2026-04-04T10:50Z', policy: 'open', haltOnRollback: true, status: 'healthy' },
  { channel: 'beta', app: 'va', current: 30, previous: 29, served: 2, maxDevices: null, minHealthyHours: 24, healthyHours: 504, promotedBy: 'ci', promotedAt: '2026-04-04T10:50Z', policy: 'open', haltOnRollback: false, status: 'healthy' },

  { channel: 'general', app: 'vc', current: 12, previous: 11, served: 24, maxDevices: null, minHealthyHours: 72, healthyHours: 240, promotedBy: 'tony', promotedAt: '2026-04-16T11:00Z', policy: 'open', haltOnRollback: true, status: 'healthy' },
  { channel: 'beta', app: 'vc', current: 12, previous: 11, served: 2, maxDevices: null, minHealthyHours: 24, healthyHours: 240, promotedBy: 'ci', promotedAt: '2026-04-16T11:00Z', policy: 'open', haltOnRollback: false, status: 'healthy' },

  { channel: 'general', app: 'launcher', current: 17, previous: 16, served: 75, maxDevices: null, minHealthyHours: 168, healthyHours: 816, promotedBy: 'super', promotedAt: '2026-03-21T10:00Z', policy: 'open', haltOnRollback: true, status: 'healthy' },
  { channel: 'beta', app: 'launcher', current: 18, previous: 17, served: 4, maxDevices: null, minHealthyHours: 48, healthyHours: 312, promotedBy: 'ci', promotedAt: '2026-04-11T11:20Z', policy: 'open', haltOnRollback: false, status: 'healthy' },
];

// Deterministic 75-device fleet across NZ/Pacific clusters.
const clusters = [
  { name: 'Auckland',          lat: -36.848, lng: 174.763, n: 18 },
  { name: 'Wellington',        lat: -41.287, lng: 174.778, n: 10 },
  { name: 'Christchurch',      lat: -43.532, lng: 172.636, n: 8 },
  { name: 'Dev bench',         lat: -36.85,  lng: 174.76,  n: 3 },
  { name: 'Pacific crossing',  lat: -32.0,   lng: -175.0,  n: 6 },
  { name: 'Tasman Sea',        lat: -40.0,   lng: 160.0,   n: 5 },
  { name: 'Fiji',              lat: -18.124, lng: 178.450, n: 4 },
  { name: 'Tonga',             lat: -21.179, lng: -175.198, n: 3 },
  { name: 'Solomon Islands',   lat: -9.443,  lng: 159.950, n: 3 },
  { name: 'New Caledonia',     lat: -22.275, lng: 166.458, n: 3 },
  { name: 'Norfolk Island',    lat: -29.040, lng: 167.955, n: 2 },
  { name: 'Chatham Islands',   lat: -43.954, lng: -176.560, n: 2 },
  { name: 'Stewart Island',    lat: -46.898, lng: 168.128, n: 2 },
  { name: 'Bay of Islands',    lat: -35.227, lng: 174.085, n: 2 },
  { name: 'Marlborough Sounds',lat: -41.270, lng: 173.960, n: 2 },
  { name: 'Rotorua Lakes',     lat: -38.137, lng: 176.250, n: 2 },
];

const boatNames = [
  'Southern Cross', 'Sea Sprite', 'Wind Dancer', 'Pacific Pearl', 'Blue Horizon',
  'Kiwi Cruiser', 'Tidal Spirit', 'Ocean Ranger', 'Harbour Light', 'Storm Petrel',
];

function makeMockDevices(): OtaDevice[] {
  const out: OtaDevice[] = [];
  let idx = 0;
  let s = 42;
  const rand = () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
  for (const c of clusters) {
    for (let i = 0; i < c.n; i++) {
      const lat = c.lat + (rand() - 0.5) * 0.8;
      const lng = c.lng + (rand() - 0.5) * 0.8;
      const isDevBench = c.name === 'Dev bench';
      const role: OtaDevice['role'] = isDevBench
        ? 'admin'
        : rand() < 0.05
          ? 'tester_plus'
          : rand() < 0.12
            ? 'tester'
            : 'user';
      const conn: OtaDevice['connectivity'] = isDevBench
        ? 'wifi'
        : c.name === 'Pacific crossing' ? (rand() < 0.6 ? 'satellite' : 'none')
        : c.name === 'Tasman Sea'       ? (rand() < 0.5 ? 'cellular'  : 'satellite')
        : rand() < 0.65 ? 'wifi'
        : rand() < 0.85 ? 'cellular'
        : rand() < 0.95 ? 'satellite'
        : 'none';
      const channel: OtaDevice['channel'] =
        role === 'admin' ? 'beta'
        : role === 'tester_plus' ? 'beta'
        : role === 'tester' ? 'general'
        : 'general';
      let vnVc: number;
      if (channel === 'beta') vnVc = 213;
      else if (idx < 4 && rand() < 0.4) vnVc = 211;
      else vnVc = rand() < 0.5 ? 209 : 208;
      const sog = isDevBench
        ? 0
        : c.name.includes('crossing') || c.name.includes('Tasman')
          ? 5 + rand() * 4
          : rand() < 0.6 ? 0 : rand() * 6;
      const underway = sog > 0.5;
      const offlineDays = conn === 'none' ? Math.floor(rand() * 20) : 0;
      const name = boatNames[idx % boatNames.length] + (idx >= boatNames.length ? ` ${idx}` : '');
      out.push({
        id: `dev_${String(idx).padStart(4, '0')}`,
        name,
        vessel: name,
        cluster: c.name,
        lat,
        lng,
        role,
        connectivity: conn,
        channel,
        vnVc,
        model: rand() < 0.3 ? 'Vortex Scout Solo' : rand() < 0.7 ? 'Active 8 Pro' : 'Scout Mk2',
        sog: Math.round(sog * 10) / 10,
        underway,
        offlineDays,
        crashes: 0,
        lastCheckIn: offlineDays ? `${offlineDays}d ago` : `${Math.floor(rand() * 22) + 1}m ago`,
        autoRollback: false,
      });
      idx++;
    }
  }
  const victim = out.find((d) => d.vnVc === 211 && d.connectivity !== 'none');
  if (victim) {
    victim.autoRollback = true;
    victim.crashes = 4;
    victim.vnVc = 208;
    victim.flaggedBuild = 211;
  }
  return out;
}

export const MOCK_DEVICES: OtaDevice[] = makeMockDevices();

export const MOCK_AUDIT: AuditEntry[] = [
  { t: '2026-04-24T02:14Z', actor: 'ci', action: 'promote', app: 'vn', channel: 'beta', from: 212, to: 213, reason: 'auto-promote from green CI run-4821 (standard + platform APKs)', smoke: 'pass' },
  { t: '2026-04-23T22:00Z', actor: 'tony', action: 'promote', app: 'vn', channel: 'general', from: 208, to: 211, reason: '3.74.0 — 132h soak on beta, zero crashes. Starting exposure rollout maxDevices=8.', smoke: 'pass' },
  { t: '2026-04-21T10:33Z', actor: 'ci', action: 'promote', app: 'th', channel: 'beta', from: 97, to: 98, reason: 'auto-promote from CI run-4817', smoke: 'pass' },
  { t: '2026-04-18T12:15Z', actor: 'ci', action: 'promote', app: 'vt', channel: 'beta', from: 44, to: 45, reason: 'auto-promote from CI run-4802', smoke: 'pass' },
  { t: '2026-04-17T11:44Z', actor: 'tony', action: 'widen-rollout', app: 'vn', channel: 'general', from: null, to: null, reason: 'widen 3→6 for vn 209 rollout', smoke: null },
  { t: '2026-04-16T11:00Z', actor: 'tony', action: 'promote', app: 'vc', channel: 'general', from: 11, to: 12, reason: '0.3.1 GA', smoke: 'pass' },
  { t: '2026-04-15T14:12Z', actor: 'tony', action: 'promote', app: 'th', channel: 'general', from: 96, to: 97, reason: '1.11.3 rolling out', smoke: 'pass' },
  { t: '2026-04-11T11:20Z', actor: 'ci', action: 'promote', app: 'launcher', channel: 'beta', from: 17, to: 18, reason: 'launcher candidate — 168h soak before general', smoke: 'pass' },
  { t: '2026-04-09T08:40Z', actor: 'tony', action: 'halt', app: 'vn', channel: 'general', from: null, to: null, reason: 'halting vn 210 rollout — cache corruption false alarm', smoke: null },
  { t: '2026-04-09T10:55Z', actor: 'tony', action: 'resume', app: 'vn', channel: 'general', from: null, to: null, reason: 'resume after investigation', smoke: null },
  { t: '2026-04-06T09:00Z', actor: 'sarah', action: 'promote', app: 'vt', channel: 'general', from: 43, to: 44, reason: '0.9.1 GA', smoke: 'pass' },
  { t: '2026-04-04T10:50Z', actor: 'tony', action: 'promote', app: 'va', channel: 'general', from: 29, to: 30, reason: '0.5.4 GA', smoke: 'pass' },
  { t: '2026-04-02T14:10Z', actor: 'ci', action: 'promote', app: 'vn', channel: 'beta', from: 207, to: 208, reason: 'auto-promote 3.73.0', smoke: 'pass' },
  { t: '2026-03-21T10:00Z', actor: 'super', action: 'promote', app: 'launcher', channel: 'general', from: 16, to: 17, reason: 'launcher 2.3.4 — full soak passed', smoke: 'pass' },
  { t: '2026-03-15T09:21Z', actor: 'tony', action: 'prune-build', app: 'vn', channel: null, from: 203, to: null, reason: 'prune build vn 203 — superseded and older than 30d', smoke: null },
];
